import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import { Chart } from "react-google-charts";

const API = process.env.REACT_APP_SERVER_URL || "";

const STATUS_COLORS = {
  sent: "default",
  queued: "warning",
  failed: "error",
  bounced: "error",
  complained: "error",
};

const dateInput = (d) => {
  if (!d) return "";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  return x.toISOString().slice(0, 10);
};

async function jsonFetch(url) {
  const res = await fetch(`${API}${url}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function StatTile({ label, value, tone = "default", footer }) {
  const palette = {
    default: { bg: "#f8fafc", fg: "#0f172a", border: "#e2e8f0" },
    primary: { bg: "#f1f8ff", fg: "#0278c2", border: "#bee0f8" },
    warning: { bg: "#fffbeb", fg: "#92400e", border: "#fde68a" },
    danger: { bg: "#fef2f2", fg: "#b91c1c", border: "#fecaca" },
  }[tone];
  return (
    <Card
      sx={{
        borderRadius: 3,
        border: `1px solid ${palette.border}`,
        background: palette.bg,
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        height: "100%",
      }}
    >
      <CardContent>
        <Typography
          sx={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: 1,
            color: "#64748b",
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 26,
            fontWeight: 700,
            color: palette.fg,
            mt: 0.5,
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>
        {footer ? (
          <Typography sx={{ fontSize: 12, color: "#475569", mt: 0.5 }}>
            {footer}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

function BreakdownTable({ title, rows }) {
  return (
    <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", height: "100%" }}>
      <CardContent>
        <Typography
          sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a", mb: 1.5 }}
        >
          {title}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Sends
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Open %
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Click %
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows && rows.length ? (
                rows.map((r) => (
                  <TableRow key={r.name} hover>
                    <TableCell>{r.name}</TableCell>
                    <TableCell align="right">{r.sends}</TableCell>
                    <TableCell align="right">{r.open_rate}%</TableCell>
                    <TableCell align="right">{r.click_rate}%</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ color: "#94a3b8" }}>
                    No data in this range
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

export default function EmailAnalytics() {
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

  const [from, setFrom] = useState(dateInput(monthAgo));
  const [to, setTo] = useState(dateInput(today));
  const [emailType, setEmailType] = useState("");
  const [emailPurpose, setEmailPurpose] = useState("");
  const [sendStatus, setSendStatus] = useState("");
  const [provider, setProvider] = useState("");
  const [recipient, setRecipient] = useState("");

  const [options, setOptions] = useState({
    types: [],
    purposes: [],
    statuses: [],
    providers: [],
  });

  const [summary, setSummary] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [sends, setSends] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const limit = 25;

  const [openSend, setOpenSend] = useState(null);
  const [openSendLoading, setOpenSendLoading] = useState(false);

  // New: engagement filter on the sends table + view tab + recipients table
  const [engagement, setEngagement] = useState("all"); // all|opened|clicked|none
  const [view, setView] = useState("sends"); // sends|recipients
  const [recipients, setRecipients] = useState({ rows: [], total: 0 });
  const [openRecipient, setOpenRecipient] = useState(null);
  const [openRecipientLoading, setOpenRecipientLoading] = useState(false);

  const filterQs = useMemo(() => {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (emailType) params.set("emailType", emailType);
    if (emailPurpose) params.set("emailPurpose", emailPurpose);
    if (sendStatus) params.set("status", sendStatus);
    if (provider) params.set("provider", provider);
    if (recipient) params.set("recipient", recipient);
    return params.toString();
  }, [from, to, emailType, emailPurpose, sendStatus, provider, recipient]);

  const sendsQs = useMemo(() => {
    const params = new URLSearchParams(filterQs);
    if (engagement !== "all") params.set("engagement", engagement);
    return params.toString();
  }, [filterQs, engagement]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b, l, r] = await Promise.all([
        jsonFetch(`/admin/email-analytics/summary?${filterQs}`),
        jsonFetch(`/admin/email-analytics/breakdown?${filterQs}`),
        jsonFetch(
          `/admin/email-analytics/sends?${sendsQs}&limit=${limit}&offset=${
            (page - 1) * limit
          }`,
        ),
        jsonFetch(`/admin/email-analytics/recipients?${filterQs}&limit=500`),
      ]);
      setSummary(s);
      setBreakdown(b);
      setSends(l);
      setRecipients(r);
    } catch (err) {
      toast.error(err?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [filterQs, sendsQs, page]);

  const openRecipientTimeline = async (email) => {
    setOpenRecipientLoading(true);
    setOpenRecipient({ loading: true, recipient_email: email });
    try {
      const d = await jsonFetch(
        `/admin/email-analytics/recipients/${encodeURIComponent(email)}/timeline`,
      );
      setOpenRecipient(d);
    } catch (err) {
      toast.error(err?.message || "Failed to load recipient");
      setOpenRecipient(null);
    } finally {
      setOpenRecipientLoading(false);
    }
  };

  useEffect(() => {
    jsonFetch("/admin/email-analytics/filter-options")
      .then((d) => setOptions(d))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const openDetail = async (id) => {
    setOpenSendLoading(true);
    setOpenSend({ loading: true });
    try {
      const d = await jsonFetch(`/admin/email-analytics/sends/${id}`);
      setOpenSend(d);
    } catch (err) {
      toast.error(err?.message || "Failed to load send");
      setOpenSend(null);
    } finally {
      setOpenSendLoading(false);
    }
  };

  const trendChartData = useMemo(() => {
    if (!summary?.trend?.length)
      return [["Day", "Sends", "Opens", "Clicks"], ["—", 0, 0, 0]];
    return [
      ["Day", "Sends", "Opens", "Clicks"],
      ...summary.trend.map((d) => [
        String(d.day).slice(5),
        Number(d.sends || 0),
        Number(d.opens || 0),
        Number(d.clicks || 0),
      ]),
    ];
  }, [summary]);

  const totals = summary?.totals || {};
  const rates = summary?.rates || {};

  return (
    <Box sx={{ p: 3, background: "#fafbfc", minHeight: "100vh", width: "100%" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>
            Email analytics
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#475569" }}>
            Track sends, opens, clicks and bounces across every campaign.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadAll} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Filters */}
      <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={emailType}
                  onChange={(e) => {
                    setEmailType(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {options.types.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Purpose</InputLabel>
                <Select
                  label="Purpose"
                  value={emailPurpose}
                  onChange={(e) => {
                    setEmailPurpose(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {options.purposes.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={sendStatus}
                  onChange={(e) => {
                    setSendStatus(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {options.statuses.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Provider</InputLabel>
                <Select
                  label="Provider"
                  value={provider}
                  onChange={(e) => {
                    setProvider(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {options.providers.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Recipient email contains…"
                placeholder="e.g. @easexpense.com"
                value={recipient}
                onChange={(e) => {
                  setRecipient(e.target.value);
                  setPage(1);
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tiles */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <StatTile label="Total sends" value={totals.sends ?? 0} tone="primary" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Open rate"
            value={`${rates.open_rate ?? 0}%`}
            footer={`${totals.unique_opens ?? 0} unique opens`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Click rate"
            value={`${rates.click_rate ?? 0}%`}
            footer={`${totals.unique_clicks ?? 0} unique clicks`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Bounces + complaints"
            value={`${(totals.bounced ?? 0) + (totals.complained ?? 0)}`}
            tone="danger"
            footer={`${totals.failed ?? 0} send failures`}
          />
        </Grid>
      </Grid>

      {/* Trend chart */}
      <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", mb: 2 }}>
        <CardContent>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a", mb: 1 }}>
            Sends, opens & clicks over time
          </Typography>
          <Chart
            chartType="AreaChart"
            data={trendChartData}
            width="100%"
            height="280px"
            options={{
              backgroundColor: "transparent",
              legend: { position: "top", alignment: "end" },
              chartArea: { left: 60, top: 30, right: 20, bottom: 40, width: "92%" },
              colors: ["#1a9aea", "#22c55e", "#f59e0b"],
              areaOpacity: 0.18,
              hAxis: { textStyle: { fontSize: 11 } },
              vAxis: { textStyle: { fontSize: 11 }, minValue: 0 },
            }}
          />
        </CardContent>
      </Card>

      {/* Breakdown */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <BreakdownTable title="Performance by email type" rows={breakdown?.by_type} />
        </Grid>
        <Grid item xs={12} md={6}>
          <BreakdownTable title="Performance by purpose" rows={breakdown?.by_purpose} />
        </Grid>
      </Grid>

      {/* View switcher: Sends list ↔ Recipients */}
      <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", mb: 2 }}>
        <Tabs
          value={view}
          onChange={(_, v) => setView(v)}
          sx={{
            borderBottom: "1px solid #e2e8f0",
            px: 2,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
          }}
        >
          <Tab
            value="sends"
            label={`Sends (${sends.total || 0})`}
          />
          <Tab
            value="recipients"
            label={`Recipients (${recipients.total || 0})`}
          />
        </Tabs>

        {view === "sends" && (
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                Engagement:
              </Typography>
              {[
                { key: "all", label: "All" },
                { key: "opened", label: "Opened" },
                { key: "clicked", label: "Clicked" },
                { key: "none", label: "No activity" },
              ].map((opt) => (
                <Chip
                  key={opt.key}
                  label={opt.label}
                  size="small"
                  clickable
                  onClick={() => {
                    setEngagement(opt.key);
                    setPage(1);
                  }}
                  color={engagement === opt.key ? "primary" : "default"}
                  variant={engagement === opt.key ? "filled" : "outlined"}
                  sx={{ height: 24, fontSize: 12 }}
                />
              ))}
            </Stack>
            <Typography sx={{ fontSize: 12, color: "#64748b" }}>
              {sends.total} sends
            </Typography>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Sent</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Recipient</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    Opened
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    Clicked
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sends.rows && sends.rows.length ? (
                  sends.rows.map((r) => (
                    <TableRow
                      key={r.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => openDetail(r.id)}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell>{r.recipient_email}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.email_type || "—"}
                          size="small"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell>{r.email_purpose || "—"}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 320,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {r.subject || "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={r.send_status || "—"}
                          size="small"
                          color={STATUS_COLORS[r.send_status] || "default"}
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {r.opened ? (
                          <Tooltip title={r.last_open_at ? `Last open: ${new Date(r.last_open_at).toLocaleString()}` : "Opened"}>
                            <span style={{ color: "#15803d", fontWeight: 600 }}>
                              {r.opens > 1 ? `✓ ${r.opens}` : "✓"}
                            </span>
                          </Tooltip>
                        ) : ""}
                      </TableCell>
                      <TableCell align="center">
                        {r.clicked ? (
                          <Tooltip title={r.last_click_at ? `Last click: ${new Date(r.last_click_at).toLocaleString()}` : "Clicked"}>
                            <span style={{ color: "#0278c2", fontWeight: 600 }}>
                              {r.clicks > 1 ? `✓ ${r.clicks}` : "✓"}
                            </span>
                          </Tooltip>
                        ) : ""}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ color: "#94a3b8" }}>
                      No sends match this filter
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
            <Pagination
              count={Math.max(Math.ceil(sends.total / limit), 1)}
              page={page}
              onChange={(_, p) => setPage(p)}
              size="small"
            />
          </Box>
        </CardContent>
        )}

        {view === "recipients" && (
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Typography sx={{ fontSize: 13, color: "#64748b" }}>
              One row per recipient. Click any row for their full email history.
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748b" }}>
              {recipients.total} recipients
            </Typography>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Recipient</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Sends
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Opens (unique)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Clicks (unique)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Open %
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Click %
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recipients.rows && recipients.rows.length ? (
                  recipients.rows.map((r) => (
                    <TableRow
                      key={r.recipient_email}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => openRecipientTimeline(r.recipient_email)}
                    >
                      <TableCell>{r.recipient_email}</TableCell>
                      <TableCell align="right">{r.sends}</TableCell>
                      <TableCell align="right">
                        {r.sends_opened} ({r.opens} events)
                      </TableCell>
                      <TableCell align="right">
                        {r.sends_clicked} ({r.clicks} events)
                      </TableCell>
                      <TableCell align="right">{r.open_rate}%</TableCell>
                      <TableCell align="right">{r.click_rate}%</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {r.last_event_at
                          ? new Date(r.last_event_at).toLocaleString()
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ color: "#94a3b8" }}>
                      No recipients in this range
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
        )}
      </Card>

      {/* Send detail dialog */}
      <Dialog
        open={!!openSend}
        onClose={() => setOpenSend(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Send detail
          <IconButton onClick={() => setOpenSend(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {openSendLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : openSend?.send ? (
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Recipient
                  </Typography>
                  <Typography>{openSend.send.recipient_email}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Type
                  </Typography>
                  <Typography>{openSend.send.email_type}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Purpose
                  </Typography>
                  <Typography>{openSend.send.email_purpose || "—"}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Subject
                  </Typography>
                  <Typography>{openSend.send.subject || "—"}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Provider
                  </Typography>
                  <Typography>{openSend.send.provider || "—"}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Status
                  </Typography>
                  <Typography>{openSend.send.send_status}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Sent at
                  </Typography>
                  <Typography>
                    {openSend.send.sent_at
                      ? new Date(openSend.send.sent_at).toLocaleString()
                      : "—"}
                  </Typography>
                </Grid>
                {openSend.send.send_error ? (
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: "#b91c1c" }}>
                      Send error
                    </Typography>
                    <Typography sx={{ color: "#b91c1c", fontSize: 13 }}>
                      {openSend.send.send_error}
                    </Typography>
                  </Grid>
                ) : null}
              </Grid>

              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a", mt: 2 }}>
                Event timeline ({openSend.events?.length || 0})
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>When</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Link</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>UA / IP</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {openSend.events?.length ? (
                      openSend.events.map((e) => (
                        <TableRow key={e.id} hover>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {e.event_at
                              ? new Date(e.event_at).toLocaleString()
                              : "—"}
                          </TableCell>
                          <TableCell>
                            <Chip label={e.event_type} size="small" />
                          </TableCell>
                          <TableCell>{e.source || "—"}</TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 220,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {e.link_url || "—"}
                          </TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 220,
                              fontSize: 11,
                              color: "#64748b",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {[e.user_agent, e.ip_address].filter(Boolean).join(" · ") || "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: "#94a3b8" }}>
                          No events recorded yet — opens and clicks land here as
                          they happen.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Recipient timeline dialog */}
      <Dialog
        open={!!openRecipient}
        onClose={() => setOpenRecipient(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>
            Recipient: <strong>{openRecipient?.recipient_email || "—"}</strong>
          </span>
          <IconButton onClick={() => setOpenRecipient(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {openRecipientLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : openRecipient?.sends ? (
            <Stack spacing={2}>
              <Typography sx={{ fontSize: 13, color: "#475569" }}>
                {openRecipient.sends.length} send
                {openRecipient.sends.length === 1 ? "" : "s"} to this address,{" "}
                {openRecipient.events?.length || 0} event
                {(openRecipient.events?.length || 0) === 1 ? "" : "s"} recorded.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Sent</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Events</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {openRecipient.sends.map((s) => {
                      const sendEvents = (openRecipient.events || []).filter(
                        (e) => e.email_send_id === s.id,
                      );
                      const opens = sendEvents.filter((e) => e.event_type === "open").length;
                      const clicks = sendEvents.filter((e) => e.event_type === "click").length;
                      return (
                        <TableRow
                          key={s.id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            setOpenRecipient(null);
                            openDetail(s.id);
                          }}
                        >
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {s.sent_at
                              ? new Date(s.sent_at).toLocaleString()
                              : "—"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={s.email_type || "—"}
                              size="small"
                              sx={{ height: 20, fontSize: 11 }}
                            />
                          </TableCell>
                          <TableCell>{s.email_purpose || "—"}</TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 220,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {s.subject || "—"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={s.send_status || "—"}
                              size="small"
                              color={STATUS_COLORS[s.send_status] || "default"}
                              sx={{ height: 20, fontSize: 11 }}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>
                            {opens > 0 && (
                              <span style={{ color: "#15803d", marginRight: 8 }}>
                                {opens} open{opens === 1 ? "" : "s"}
                              </span>
                            )}
                            {clicks > 0 && (
                              <span style={{ color: "#0278c2" }}>
                                {clicks} click{clicks === 1 ? "" : "s"}
                              </span>
                            )}
                            {opens === 0 && clicks === 0 && (
                              <span style={{ color: "#94a3b8" }}>—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
